import { useEffect, useMemo, useRef, useState } from 'react';
import { Search, CornerDownLeft, Sun, Moon, LogOut, LayoutDashboard } from 'lucide-react';
import { useTheme } from '@/hooks/useTheme';
import { useAuth } from '@/contexts/AuthContext';

export interface PaletteItem {
  tab: string;
  label: string;
  group: string;
  keywords?: string;
}

export default function CommandPalette({
  open,
  onClose,
  items,
  onNavigate,
}: {
  open: boolean;
  onClose: () => void;
  items: PaletteItem[];
  onNavigate: (tab: string) => void;
}) {
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const { theme, toggleTheme } = useTheme();
  const { logout } = useAuth();

  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIdx(0);
      setTimeout(() => inputRef.current?.focus(), 10);
    }
  }, [open]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = items.filter((i) => !q || i.label.toLowerCase().includes(q) || (i.keywords || '').toLowerCase().includes(q));
    if (!q) return base;
    // rank: label startsWith > label includes > keywords
    return [...base].sort((a, b) => {
      const sa = a.label.toLowerCase().startsWith(q) ? 0 : 1;
      const sb = b.label.toLowerCase().startsWith(q) ? 0 : 1;
      return sa - sb;
    });
  }, [items, query]);

  if (!open) return null;

  const actions = [
    { id: '__theme__', label: theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode', group: 'Actions', icon: theme === 'dark' ? <Sun size={16} /> : <Moon size={16} /> },
    { id: '__logout__', label: 'Logout', group: 'Actions', icon: <LogOut size={16} /> },
  ];

  const all = [...results.map((r) => ({ ...r, icon: <LayoutDashboard size={16} />, kind: 'page' as const })), ...actions.map((a) => ({ tab: a.id, label: a.label, group: a.group, icon: a.icon, kind: 'action' as const }))];

  const pick = (idx: number) => {
    const item = all[idx];
    if (!item) return;
    if (item.kind === 'action') {
      if (item.tab === '__theme__') toggleTheme();
      if (item.tab === '__logout__') logout();
      onClose();
      return;
    }
    onNavigate(item.tab);
    onClose();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, all.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); pick(activeIdx); }
    else if (e.key === 'Escape') { onClose(); }
  };

  let lastGroup = '';

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh] px-4" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <div className="relative w-full max-w-lg bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-slate-700 overflow-hidden animate-slide-down">
        <div className="flex items-center gap-3 px-4 h-13 border-b border-gray-100 dark:border-slate-700">
          <Search size={18} className="text-gray-400 shrink-0 dark:text-slate-500" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setActiveIdx(0); }}
            onKeyDown={onKeyDown}
            placeholder="Search pages & actions…"
            className="flex-1 h-12 bg-transparent text-sm text-gray-800 dark:text-slate-200 placeholder-gray-400 dark:placeholder-slate-500 outline-none"
          />
          <kbd className="text-[10px] font-medium text-gray-400 dark:text-slate-500 border border-gray-200 dark:border-slate-600 rounded px-1.5 py-0.5">ESC</kbd>
        </div>
        <div className="max-h-72 overflow-y-auto py-2">
          {all.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-gray-400 dark:text-slate-500">No results for “{query}”</div>
          )}
          {all.map((item, i) => {
            const showGroup = item.group !== lastGroup;
            lastGroup = item.group;
            return (
              <div key={item.tab + item.label}>
                {showGroup && (
                  <div className="px-4 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-slate-500">{item.group}</div>
                )}
                <button
                  onClick={() => pick(i)}
                  onMouseEnter={() => setActiveIdx(i)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left transition ${i === activeIdx ? 'bg-indigo-50 dark:bg-indigo-950/50 text-indigo-700 dark:text-indigo-300' : 'text-gray-700 dark:text-slate-300'}`}
                >
                  <span className={i === activeIdx ? 'text-indigo-500' : 'text-gray-400 dark:text-slate-500'}>{item.icon}</span>
                  <span className="flex-1 truncate">{item.label}</span>
                  {i === activeIdx && <CornerDownLeft size={13} className="text-indigo-400" />}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
